// src/pages/TripPlanner/components/SuggestionList.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  ListItemButton, 
  Chip, 
  IconButton, 
  Button, 
  Divider,
  CircularProgress,
  Collapse
} from '@mui/material';
import { 
  Lightbulb as LightbulbIcon,
  LocationOn as LocationIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  LocalActivity as ActivityIcon,
  Hiking as HikingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AddCircle as AddIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

// Types for different suggestion categories
interface BaseSuggestion {
  id: string;
  title: string;
  description: string;
  source?: string;
  tags: string[];
  confidence?: number; // 0-100 indicating AI confidence
}

interface ActivitySuggestion extends BaseSuggestion {
  type: 'activity';
  duration?: string;
  cost?: number;
  bestTime?: string;
  location: string;
  coordinates?: [number, number];
}

interface AccommodationSuggestion extends BaseSuggestion {
  type: 'accommodation';
  priceRange?: string;
  address: string;
  amenities?: string[];
  coordinates?: [number, number];
}

interface FoodSuggestion extends BaseSuggestion {
  type: 'food';
  cuisine: string;
  priceRange?: string;
  address?: string;
  coordinates?: [number, number];
}

interface DestinationSuggestion extends BaseSuggestion {
  type: 'destination';
  country: string;
  bestTimeToVisit?: string;
  coordinates?: [number, number];
}

type Suggestion = ActivitySuggestion | AccommodationSuggestion | FoodSuggestion | DestinationSuggestion;

// Props for the suggestion list component
interface SuggestionListProps {
  suggestions: Suggestion[];
  destination?: string;
  isLoading?: boolean;
  onAddSuggestion: (suggestion: Suggestion) => void;
  onGenerateSuggestions?: () => void;
  title?: string;
  emptyMessage?: string;
}

const SuggestionList: React.FC<SuggestionListProps> = ({
  suggestions,
  destination,
  isLoading = false,
  onAddSuggestion,
  onGenerateSuggestions,
  title = "AI Suggestions",
  emptyMessage = "No suggestions available yet."
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  
  // Toggle expanded view for a suggestion
  const handleExpandClick = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };
  
  // Filter suggestions by type
  const handleFilterChange = (type: string | null) => {
    setSelectedType(type === selectedType ? null : type);
  };
  
  // Get filtered suggestions
  const filteredSuggestions = selectedType 
    ? suggestions.filter(suggestion => suggestion.type === selectedType)
    : suggestions;
  
  // Get icon based on suggestion type
  const getSuggestionIcon = (suggestion: Suggestion) => {
    switch (suggestion.type) {
      case 'activity':
        return suggestion.tags.includes('hiking') || suggestion.tags.includes('outdoor') 
          ? <HikingIcon color="primary" />
          : <ActivityIcon color="primary" />;
      case 'accommodation':
        return <HotelIcon color="primary" />;
      case 'food':
        return <RestaurantIcon color="primary" />;
      case 'destination':
        return <LocationIcon color="primary" />;
      default:
        return <LightbulbIcon color="primary" />;
    }
  };
  
  // Get display name for type
  const getTypeDisplayName = (type: string) => {
    switch (type) {
      case 'activity': return 'Activities';
      case 'accommodation': return 'Accommodations';
      case 'food': return 'Food & Dining';
      case 'destination': return 'Destinations';
      default: return 'Suggestions';
    }
  };
  
  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <LightbulbIcon sx={{ mr: 1 }} />
            {title}
          </Typography>
          
          {destination && (
            <Typography variant="body2" color="text.secondary">
              For your trip to {destination}
            </Typography>
          )}
        </Box>
        
        {onGenerateSuggestions && (
          <Button
            variant="outlined"
            onClick={onGenerateSuggestions}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <LightbulbIcon />}
          >
            {isLoading ? 'Generating...' : 'Get Suggestions'}
          </Button>
        )}
      </Box>
      
      {suggestions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
            <FilterIcon fontSize="small" sx={{ mr: 0.5 }} />
            Filter:
          </Typography>
          
          {['activity', 'accommodation', 'food', 'destination'].map((type) => (
            suggestions.some(s => s.type === type) && (
              <Chip
                key={type}
                label={getTypeDisplayName(type)}
                onClick={() => handleFilterChange(type)}
                color={selectedType === type ? 'primary' : 'default'}
                variant={selectedType === type ? 'filled' : 'outlined'}
                size="small"
              />
            )
          ))}
        </Box>
      )}
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredSuggestions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <List>
          {filteredSuggestions.map((suggestion) => (
            <React.Fragment key={suggestion.id}>
              <ListItem 
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="add"
                    onClick={() => onAddSuggestion(suggestion)}
                  >
                    <AddIcon />
                  </IconButton>
                }
              >
                <ListItemButton onClick={() => handleExpandClick(suggestion.id)}>
                  <ListItemIcon>
                    {getSuggestionIcon(suggestion)}
                  </ListItemIcon>
                  <ListItemText
                    primary={suggestion.title}
                    secondary={
                      <>
                        <Typography
                          sx={{ display: 'inline' }}
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          {suggestion.type === 'food' 
                            ? `${(suggestion as FoodSuggestion).cuisine} cuisine`
                            : suggestion.type === 'destination'
                            ? (suggestion as DestinationSuggestion).country
                            : suggestion.type === 'accommodation'
                            ? (suggestion as AccommodationSuggestion).priceRange
                            : (suggestion as ActivitySuggestion).duration}
                        </Typography>
                        {expandedId !== suggestion.id && (
                          <>
                            {" — "}
                            {suggestion.description.length > 60
                              ? `${suggestion.description.substring(0, 60)}...`
                              : suggestion.description}
                          </>
                        )}
                      </>
                    }
                  />
                  {expandedId === suggestion.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
              </ListItem>
              
              <Collapse in={expandedId === suggestion.id} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 9, pr: 5, pb: 2 }}>
                  <Typography variant="body2" paragraph>
                    {suggestion.description}
                  </Typography>
                  
                  {/* Details based on suggestion type */}
                  {suggestion.type === 'activity' && (
                    <Box sx={{ mb: 2 }}>
                      {(suggestion as ActivitySuggestion).duration && (
                        <Typography variant="body2">
                          <strong>Duration:</strong> {(suggestion as ActivitySuggestion).duration}
                        </Typography>
                      )}
                      {(suggestion as ActivitySuggestion).cost !== undefined && (
                        <Typography variant="body2">
                          <strong>Estimated Cost:</strong> ${(suggestion as ActivitySuggestion).cost}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Location:</strong> {(suggestion as ActivitySuggestion).location}
                      </Typography>
                    </Box>
                  )}
                  
                  {suggestion.type === 'accommodation' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Price Range:</strong> {(suggestion as AccommodationSuggestion).priceRange || 'Not specified'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Address:</strong> {(suggestion as AccommodationSuggestion).address}
                      </Typography>
                      {(suggestion as AccommodationSuggestion).amenities && (
                        <Typography variant="body2">
                          <strong>Amenities:</strong> {(suggestion as AccommodationSuggestion).amenities?.join(', ')}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {suggestion.type === 'food' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Cuisine:</strong> {(suggestion as FoodSuggestion).cuisine}
                      </Typography>
                      {(suggestion as FoodSuggestion).priceRange && (
                        <Typography variant="body2">
                          <strong>Price Range:</strong> {(suggestion as FoodSuggestion).priceRange}
                        </Typography>
                      )}
                      {(suggestion as FoodSuggestion).address && (
                        <Typography variant="body2">
                          <strong>Address:</strong> {(suggestion as FoodSuggestion).address}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {suggestion.type === 'destination' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Country:</strong> {(suggestion as DestinationSuggestion).country}
                      </Typography>
                      {(suggestion as DestinationSuggestion).bestTimeToVisit && (
                        <Typography variant="body2">
                          <strong>Best Time to Visit:</strong> {(suggestion as DestinationSuggestion).bestTimeToVisit}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* Tags */}
                  {suggestion.tags && suggestion.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {suggestion.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </Box>
                  )}
                  
                  {/* Source attribution */}
                  {suggestion.source && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Source: {suggestion.source}
                    </Typography>
                  )}
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => onAddSuggestion(suggestion)}
                    sx={{ mt: 1 }}
                  >
                    Add to Trip
                  </Button>
                </Box>
              </Collapse>
              
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
};

// Function to generate mock suggestions (for demo purposes)
export const generateMockSuggestions = (destination: string): Suggestion[] => {
  if (destination === 'Paris') {
    return [
      {
        id: uuidv4(),
        type: 'activity',
        title: 'Louvre Museum Tour',
        description: 'Visit the world\'s largest art museum and see masterpieces like the Mona Lisa and Venus de Milo.',
        duration: '3-4 hours',
        cost: 15,
        location: 'Rue de Rivoli, 75001 Paris',
        coordinates: [2.3376, 48.8606],
        tags: ['art', 'museum', 'history'],
        confidence: 95
      },
      {
        id: uuidv4(),
        type: 'food',
        title: 'Le Petit Bistro',
        description: 'Authentic French bistro serving classic dishes like coq au vin and beef bourguignon.',
        cuisine: 'French',
        priceRange: '€€-€€€',
        address: '5 Rue de la Huchette, 75005 Paris',
        coordinates: [2.3465, 48.8532],
        tags: ['local cuisine', 'dinner', 'romantic'],
        confidence: 88
      },
      {
        id: uuidv4(),
        type: 'accommodation',
        title: 'Hotel des Arts Montmartre',
        description: 'Charming boutique hotel in the artistic district of Montmartre, close to Sacré-Cœur.',
        priceRange: '€€-€€€',
        address: '5 Rue Tholoze, 75018 Paris',
        amenities: ['Free WiFi', 'Air conditioning', 'Breakfast included'],
        coordinates: [2.3374, 48.8863],
        tags: ['boutique', 'central', 'artistic neighborhood'],
        confidence: 92
      }
    ];
  } else if (destination === 'Tokyo') {
    return [
      {
        id: uuidv4(),
        type: 'activity',
        title: 'Teamlab Borderless Digital Art Museum',
        description: 'Immersive digital art installation where art moves freely, creating a borderless world.',
        duration: '2-3 hours',
        cost: 30,
        location: 'Odaiba, Tokyo',
        coordinates: [139.7829, 35.6263],
        tags: ['art', 'digital', 'interactive'],
        confidence: 96
      },
      {
        id: uuidv4(),
        type: 'food',
        title: 'Tsukiji Outer Market Food Tour',
        description: 'Sample fresh seafood and local specialties at the famous Tsukiji Outer Market.',
        cuisine: 'Japanese',
        priceRange: '¥¥-¥¥¥',
        address: 'Tsukiji, Chuo City, Tokyo',
        coordinates: [139.7691, 35.6654],
        tags: ['seafood', 'market', 'local cuisine'],
        confidence: 93
      }
    ];
  } else {
    return [
      {
        id: uuidv4(),
        type: 'destination',
        title: 'Barcelona',
        description: 'A vibrant city known for stunning architecture, beautiful beaches, and amazing food.',
        country: 'Spain',
        bestTimeToVisit: 'April to June, September to November',
        coordinates: [2.1734, 41.3851],
        tags: ['architecture', 'beach', 'food', 'culture'],
        confidence: 90
      },
      {
        id: uuidv4(),
        type: 'destination',
        title: 'Kyoto',
        description: 'Japan\'s cultural capital with over 1,600 Buddhist temples and 400 Shinto shrines.',
        country: 'Japan',
        bestTimeToVisit: 'March-May (cherry blossoms) or October-November (fall colors)',
        coordinates: [135.7681, 35.0116],
        tags: ['temples', 'culture', 'history', 'gardens'],
        confidence: 87
      }
    ];
  }
};

export default SuggestionList;